/* eslint-disable react/no-array-index-key */
import { AxiosResponse } from 'axios';
import type { NextPage } from 'next';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import AtExcuteButton from '@/components/atoms/AtExcuteButton';
import AtHeading from '@/components/atoms/AtHeading';
import AtLink from '@/components/atoms/AtLink';
import OrContentHeadingArea from '@/components/organisms/OrContentHeadingArea';
import OrSubButtonArea from '@/components/organisms/OrSubButtonArea';
import TemplateWithLogin from '@/components/templates/TemplateWithLogin';
import useGetChashBackInfo from '@/hooks/api/useGetChashBackInfo';
import useGetContractInfo from '@/hooks/api/useGetContractInfo';
import useGetCustomerInfo from '@/hooks/api/useGetCustomerInfo';
import useGetPickUpInfo from '@/hooks/api/useGetPickUpInfo';
import useSetLoading from '@/hooks/useSetLoading';
import { CBInfoState, CBInfoStateType } from '@/states/CBInfoState';
import { ContractInfoState, ContractInfoStateType } from '@/states/ContractInfoState';
import { CustomerInfoState, CustomerInfoStateType } from '@/states/CustomerInfoState';
import { LoginUserInfoState } from '@/states/LoginUserInfoState';
import { PickUpInfoState, PickUpInfoStateType } from '@/states/PickUpInfoState';
import { PickUpReferenceInfoState, PickUpReferenceInfoStateType } from '@/states/PickUpReferenceInfoState';
import { CBInfoResponse } from '@/types/api/v1/CBInfoResponse';
import { ContractResponse } from '@/types/api/v1/ContractInfoResponse';
import { CustomerResponse } from '@/types/api/v1/CustomerInfoResponse';
import { PickUpInfoResponse, PickupInfoList } from '@/types/api/v1/PickUpInfoResponse';
import { CustomPageProps } from '@/types/client/CutostomPageProps';
import url from '@/utils/config';
import { ApiUrl, ExternalLink, LogisticsStatus, PaymentType, PlanCode, ReturnStatus, State } from '@/utils/constants';
import formatDate from '@/utils/filters/formatDate';
import formatWeekday from '@/utils/filters/formatWeekday';
import { useRouter } from 'next/router';
const Manomamain: NextPage<CustomPageProps> = () => {
  // ログインユーザー情報
  const loginUserInfo = useRecoilValue(LoginUserInfoState);
  const router = useRouter();
  // 集荷情報の取得
  const {
    execute: getPickUpInfo
  } = useGetPickUpInfo();
  const [PickUpInfo, setPickUpInfo] = useRecoilState<PickUpReferenceInfoStateType>(PickUpReferenceInfoState);
  // 顧客情報
  const [CustomerInfo, setCustomerInfo] = useRecoilState<CustomerInfoStateType>(CustomerInfoState);
  const {
    execute: getCustomerInfo
  } = useGetCustomerInfo();
  // 契約者情報
  const [contractInfo, setContractInfo] = useRecoilState<ContractInfoStateType>(ContractInfoState);
  const {
    execute: getContractInfo
  } = useGetContractInfo();
  // キャッシュバック情報
  const [ChashBackInfo, setChashBackInfo] = useRecoilState<CBInfoStateType>(CBInfoState);
  const {
    execute: getChashBackInfo
  } = useGetChashBackInfo();
  const {
    startLoading
  } = useSetLoading();
  // 画面で入力した値を取得・セット
  const [PickUpOnlyInfo, setPickUpOnlyInfo] = useRecoilState<PickUpInfoStateType>(PickUpInfoState);
  // 集荷情報が6件以上存在するかどうかの判定
  const [isSixDatas, setIsSixDatas] = useState(false);

  // 自動解約判定
  const isContinuedUseTarget = CustomerInfo.planContinuedUseInfo?.planContinuedUseFlg === '0' && CustomerInfo.planContinuedUseInfo?.planContinuedUseTarget === '1' && CustomerInfo.contractInfo.state === State.USE_STATE;

  // 遷移先の設定
  const userClick = () => {
    router.push('/user/');
  };
  const billingListClick = () => {
    router.push('/billing/list/');
  };
  const optionClick = () => {
    router.push('/option/');
  };
  const quitClick = () => {
    router.push('/quit/');
  };
  const cashbackClick = (index: number) => {
    const cbid = ChashBackInfo.cbInfos?.filter((list, _i) => _i === index)[0].cashbackId;
    router.push({
      pathname: '/cashback',
      query: {
        cbid
      }
    });
  };

  // 今日の日付の取得
  const getDate = (addDate: number) => {
    // timezone:UTC
    const date = new Date();
    // UTC→JST
    date.setHours(date.getHours() + 9);
    date.setDate(date.getDate() + addDate);
    const formatDatePickup = date.toISOString().replace(/[^0-9]/g, '').slice(0, 8);
    // ハイフン有りの日付に成形
    return `${formatDatePickup.substring(0, 4)}${formatDatePickup.substring(4, 6)}${formatDatePickup.substring(6, 8)}`;
  };

  // ご返却状況のステータス変換
  const formatState = (collSituation: string) => {
    if (collSituation === ReturnStatus.COLLECTING_UNDAUNTED || collSituation === ReturnStatus.COLLECTING_DEMAND || collSituation === ReturnStatus.COMPLETE_UNCOLLECTIBLE) {
      return '返却中';
    }
    if (collSituation === ReturnStatus.COMPLETE_RECOVERED) {
      return '返却済み';
    }
    return null;
  };

  // 回収ステータスが「返却中」の場合赤字で表示を行う
  const isRedText = (collSituation: string) => {
    return collSituation === ReturnStatus.COLLECTING_UNDAUNTED || collSituation === ReturnStatus.COLLECTING_DEMAND || collSituation === ReturnStatus.COMPLETE_UNCOLLECTIBLE;
  };

  // 「集荷日・集荷先変更」ボタンの表示有無について
  const pickUpButton = (item: PickupInfoList) => {
    return item.itemInfoList.filter(value => value.CollSituation === ReturnStatus.COMPLETE_RECOVERED || value.CollSituation === ReturnStatus.COMPLETE_UNCOLLECTIBLE || value.CollSituation === ReturnStatus.CANCEL).length === item.itemInfoList.length;
  };

  // 「集荷日・集荷先変更」ボタンの活性・非活性
  const isPickUpButton = (index: PickupInfoList) => {
    return index.pfdDate === getDate(+1);
  };

  // 「集荷日・集荷先変更」ボタンの下の注意文言について
  const cautionWord = (index: PickupInfoList) => {
    if (index.pfdDate === getDate(+1)) {
      return '※集荷日前日は変更できません。翌日以降にお手続きください。';
    }
    if (index.pfdDate === getDate(0)) {
      return '※変更手続きは可能ですが、変更前の集荷はキャンセルできませんのでご了承ください。';
    }
    return null;
  };

  // 「集荷日・集荷先変更」ボタンを押下時
  const pickUpClick = (key: number) => {
    PickUpInfo.pickupInfoList.forEach((item, index) => {
      if (key === index) {
        setPickUpOnlyInfo({
          ...PickUpOnlyInfo,
          pickupInfo: {
            lmMatterId: item.lmMatterId,
            addressZipCode: item.addrZipCd,
            addressPrefecture: item.prefecture,
            addressCity: item.city,
            addressTown: item.town,
            addressOptional: item.buildName,
            tel: item.tel
          }
        });
      }
    });
    router.push('/pick-up/input');
  };
  const showBillDetail = CustomerInfo.paymentType === PaymentType.CREDIT_CARD_PAY || CustomerInfo.contractInfo.planCode === PlanCode.RENTAL_PLAN || CustomerInfo.contractInfo.planCode === PlanCode.KODATE_PLAN || CustomerInfo.contractInfo.planCode === PlanCode.MANSION_PLAN;
  const isDispProvOption = CustomerInfo.contractInfo.state === State.USE_STATE;
  const isButtonDisable = CustomerInfo.existProvideService && !CustomerInfo.isOrderProcessing;

  // 初期表示処理
  useEffect((): void => {
    // localStargeからの取得
    const firstOpenFlg = localStorage.getItem('firstOpenFlg');
    const hiddenCheckBoxFlg = localStorage.getItem('hiddenCheckBoxFlg');
    if (firstOpenFlg === null || firstOpenFlg === '0') {
      if (hiddenCheckBoxFlg === null || hiddenCheckBoxFlg === '0') {
        if (CustomerInfo?.planContinuedUseInfo !== null && isContinuedUseTarget) {
          window.location.href = url('/continued-use/');
        }
      }
    }
  });
  useEffect((): void => {
    callAPIGetCustomerInfo();
    callCBInfo();
    callPickUpInfo();
    callContractInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 顧客情報照会APIの呼び出し
  const callAPIGetCustomerInfo = async () => {
    startLoading(ApiUrl.Read.CUSTOMER_INFO);
    const {
      data
    }: AxiosResponse<CustomerResponse, any> = await getCustomerInfo();
    // 取得した値をセット
    setCustomerInfo({
      ...CustomerInfo,
      contractInfo: data.contractInfo,
      optionInfos: data.optionInfos,
      existProvideService: data.existProvideService,
      isOrderProcessing: data.isOrderProcessing,
      deviceInfos: data.deviceInfos,
      paymentType: data.paymentType,
      planContinuedUseInfo: data.planContinuedUseInfo
    });
  };

  // 契約情報取得APIの呼び出し
  const callContractInfo = async () => {
    const {
      data
    }: AxiosResponse<ContractResponse, any> = await getContractInfo();
    // 取得した値をセット
    setContractInfo({
      ...contractInfo,
      contractorInfo: data.contractorInfo,
      contractServiceInfo: data.contractServiceInfo
    });
  };

  // CB情報照会APIの呼び出し
  const callCBInfo = async () => {
    startLoading(ApiUrl.Read.CHASH_BACK_INFO);
    const {
      data
    }: AxiosResponse<CBInfoResponse, any> = await getChashBackInfo();
    // 取得した値をセット
    setChashBackInfo({
      ...ChashBackInfo,
      cbInfos: data.cbInfos
    });
  };

  // 集荷先情報取得APIの呼び出し
  const callPickUpInfo = async () => {
    startLoading(ApiUrl.Read.PICK_UP_INFO);
    const {
      data
    }: AxiosResponse<PickUpInfoResponse, any> = await getPickUpInfo();
    const pickupinfolist = [];
    data.pickupinfolist.forEach(item => {
      if ((item.lmState === LogisticsStatus.NEW || item.lmState === LogisticsStatus.COLLECTING || item.lmState === LogisticsStatus.COLLECTING_UNDAUNTED || item.lmState === LogisticsStatus.COLLECTING_DEMAND || item.lmState === LogisticsStatus.COMPLETE || item.lmState === LogisticsStatus.COMPLETE_RECOVERED || item.lmState === LogisticsStatus.COMPLETE_UNCOLLECTIBLE) && item.pickupFlg === '1') {
        const itemInfoList = [];
        item.itemInfoList.forEach(item2 => {
          if (item2.CollSituation === ReturnStatus.COLLECTING_UNDAUNTED || item2.CollSituation === ReturnStatus.COLLECTING_DEMAND || item2.CollSituation === ReturnStatus.COMPLETE_RECOVERED || item2.CollSituation === ReturnStatus.COMPLETE_UNCOLLECTIBLE) {
            itemInfoList.push(item2);
          }
        });
        pickupinfolist.push({
          ...item,
          itemInfoList
        });
      }
      setPickUpInfo({
        ...PickUpInfo,
        pickupInfoList: pickupinfolist.slice(0, 5)
      });
      if (pickupinfolist.length >= 6) {
        setIsSixDatas(true);
      }
    });
  };
  const jsxPlan = (): JSX.Element => {
    return <>
        <div className='taRight'>
          {isContinuedUseTarget && <AtLink href='/continued-use/'>継続利用意思確認フォーム</AtLink>}
        </div>
        <div className='formArea' key='CustomerInfoState'>
          <p>
            <span className='w130'>ご契約ステータス</span>
            <span className='fwb' data-testid='stateName'>
              {CustomerInfo.contractInfo.stateName}
            </span>
          </p>
          <p>
            <span className='w130'>ご契約プラン</span>
            <span className='fwb' data-testid='planName'>
              {CustomerInfo.contractInfo.planName}
            </span>
          </p>
          <p>
            <span className='w130'>ご利用開始日</span>
            <span suppressHydrationWarning className='fwb' data-testid='serviceStartDate'>
              {formatDate(CustomerInfo.contractInfo.serviceStartDate)}
            </span>
          </p>
          <p>
            <span className='w130'>MANOMA契約ID</span>
            <span className='fwb' data-testid='iotServiceNo'>
              {CustomerInfo.contractInfo.iotServiceNo}
            </span>
          </p>
          <OrSubButtonArea buttonName='subbotton' onClick={userClick}>
            お客さま情報・お支払い情報
          </OrSubButtonArea>
          {showBillDetail && <OrSubButtonArea buttonName='subbotton' onClick={billingListClick}>
              ご請求料金一覧
            </OrSubButtonArea>}
        </div>
        <div className='formArea'>
          <AtHeading as='h2'>ご契約オプション</AtHeading>
          <table className='monthCampaignTable'>
            <thead>
              <tr>
                <th>サービス名称</th>
                <th>状態</th>
                <th>詳細</th>
              </tr>
              {CustomerInfo.optionInfos?.map((item, index) => {
              return <tr key={index}>
                    <td data-testid='optionName'>
                      {item.servicePageUrl ? <AtLink href={item.servicePageUrl} target='_blank' rel='noreferrer' className='link' data-testid='servicePageUrl'>
                          {item.optionName}
                        </AtLink> : item.optionName}
                    </td>
                    <td data-testid='optionStateName'>{item.optionStateName}</td>
                    <td data-testid='description'>{item.description.replace(/\\n/g, '\n')}</td>
                  </tr>;
            })}
            </thead>
          </table>
          <AtHeading as='h2'>ご利用機器</AtHeading>
          <table className='monthCampaignTable'>
            <thead>
              <tr>
                <th>機器名称</th>
                <th>シリアルナンバー</th>
              </tr>
              {CustomerInfo.deviceInfos?.map((item, index) => {
              return <tr key={index}>
                    <td data-testid='deviceTypeName'>{item.deviceTypeName}</td>
                    <td data-testid='deviceSerialNo'>{item.deviceSerialNo}</td>
                  </tr>;
            })}
            </thead>
          </table>
          {isDispProvOption && isButtonDisable && <div>
              <OrSubButtonArea buttonName='subbotton' onClick={optionClick}>
                ご契約オプション・機器の追加
              </OrSubButtonArea>
              <OrSubButtonArea buttonName='subbotton' onClick={quitClick}>
                ご契約オプションの解約
              </OrSubButtonArea>
            </div>}
          <div className='taCenter mt25 txtRed'>※オプション・追加機器の解約が可能です。</div>
          <AtHeading as='h2'>注意事項</AtHeading>
          <p>
            新規申込中のオプション・追加機器がある場合は、他のオプションの申込及び解約はできません。
          </p>
        </div>
        {PickUpInfo.pickupInfoList.length > 0 && <div className='formArea'>
            <AtHeading as='h2'>集荷情報</AtHeading>
            機器返却についての詳細は
            <AtLink href={ExternalLink.RETURN_EQUIPMENT_DETAIL} target='_blank'>
              こちら
            </AtLink>
            をご参照ください。
            <br />
            返却状況が「返却中」の場合、弊社で返却が確認できていない状態となります。
            {isSixDatas && <div data-testid='sixdatas'>
                表示されていない集荷情報についてはサポート窓口へお問い合わせください。
              </div>}
            {PickUpInfo.pickupInfoList.map((item, index) => {
          return <>
                  <table className='pickupTable mt40'>
                    <tr>
                      <th colSpan={4} className='header' data-testid='pickupdata'>
                        集荷機器
                      </th>
                    </tr>
                    {item.itemInfoList.map((item2, index2) => {
                return <tbody key={index}>
                          <tr>
                            <th>機器名称{index2 + 1}</th>
                            <td data-testid='deviceNameS'>{item2.ItemTypeNameS}</td>
                          </tr>
                          <tr>
                            <th>シリアルナンバー</th>
                            <td data-testid='serialno'>{item2.SonetSerialNo}</td>
                          </tr>
                          <tr>
                            <th>ご返却状況</th>
                            <td className={isRedText(item2.CollSituation) ? 'txtRed' : ''} data-testid='collsituation'>
                              {formatState(item2.CollSituation)}
                            </td>
                          </tr>
                        </tbody>;
              })}
                    <tr>
                      <th colSpan={4} className='header'>
                        集荷先住所
                      </th>
                    </tr>
                    <tr>
                      <td colSpan={4} className='taCenter'>
                        <div data-testid='addrzipcd'>〒{item.addrZipCd}</div>
                        <div data-testid='address'>
                          {item.prefecture}
                          {item.city}
                          {item.town}
                          {item.buildName}
                        </div>
                        <div data-testid='personname'>
                          {item.personLastName}
                          {item.personFirstName}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th colSpan={4} className='header'>
                        集荷日時
                      </th>
                    </tr>
                    <tr>
                      <td colSpan={4} className='taCenter' data-testid='desireddate'>
                        {formatWeekday(item.pfdDate)} {item.pfdTimeZone}
                      </td>
                    </tr>
                  </table>
                  {!pickUpButton(item) && <>
                      <OrSubButtonArea buttonName='subbotton' onClick={() => pickUpClick(index)} disabled={isPickUpButton(item)}>
                        集荷日・集荷先変更
                      </OrSubButtonArea>
                      <div className='taCenter' data-testid='cautionword'>
                        {cautionWord(item)}
                      </div>
                    </>}
                </>;
        })}
          </div>}
        {ChashBackInfo.cbInfos?.length > 0 && <div className='formArea' data-testid='CbInfos'>
            <AtHeading as='h2'>キャッシュバック情報</AtHeading>
            <table className='monthCampaignTable'>
              <thead>
                <tr>
                  <th>キャンペーン名称</th>
                  <th>状態</th>
                  <th>キャッシュバック通知月</th>
                  <th>お手続き</th>
                </tr>
                {ChashBackInfo.cbInfos?.map((item, index) => {
              return <tr key={index}>
                      <td data-testid='cashbackName'>{item.cashbackName}</td>
                      <td data-testid='statusCodeDescription'>{item.statusCodeDescription}</td>
                      <td data-testid='cbStartDate'>{item.cbStartDate}</td>
                      <td className='btnArea' data-testid='button'>
                        {item.statusCode === 'CB01' || item.statusCode === 'CB07' ? <AtExcuteButton className='w50pPc ph5' name='subbotton' onClick={() => cashbackClick(index)}>
                            受取申込
                          </AtExcuteButton> : <p>-</p>}
                      </td>
                    </tr>;
            })}
              </thead>
            </table>
            <p className='infoLabel'>
              ※通知月中旬頃にご登録のメールアドレス宛に、キャッシュバック手続きのご案内をお送りいたします。
              <br />
              ※状態が「振込エラー」の場合、再度キャッシュバック受け取り手続きをお願いいたします。
              <br />
            </p>
          </div>}
        <div className='formArea'>
          <AtHeading as='h2'>その他のお手続き</AtHeading>
          {isDispProvOption && <p>
              MANOMA解約のお申し込みは
              <AtLink href='/service-quit/' className='ml5'>
                こちら
              </AtLink>
            </p>}
          <p>
            お問い合わせは
            <AtLink href={ExternalLink.SUPPORT_DESK_URL} target='_blank' className='ml5'>
              サポート窓口
            </AtLink>
            までお願いします。
          </p>
        </div>
      </>;
  };
  return <TemplateWithLogin loginid={loginUserInfo.username}>
      <OrContentHeadingArea>メインページ</OrContentHeadingArea>
      {jsxPlan()}
    </TemplateWithLogin>;
};
/* 画面タイトル設定 */
Manomamain.getInitialProps = () => {
  return {
    title: 'メインページ'
  };
};
export default Manomamain;